import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { KeyboardArrowRight, MoreVert, PushPinOutlined } from '@mui/icons-material';
import { Checkbox, IconButton, Menu, MenuItem, Popper, styled } from '@mui/material';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import { ConfirmationDelete, ConfirmationWithRadioWrapper } from 'components/Dialogs';
import { ConfirmationWithRadioProps } from 'components/Dialogs/ConfirmationWithRadio';
import AddMultipleMenuItem from 'components/FeaturedSpreadsheet/components/AddMultipleMenuItem';
import { DialogContext } from 'context';
import { ICell } from 'models/interfaces/spreadsheet-types/core-types';
import { kpiDataTypes } from 'pages/Financials/kpi/config/constants';
import { TCells } from 'pages/ValuationsAllocation/approaches/DiscountCashFlow/components/EffectiveTaxRateLedger/types';
import { useResponse } from 'services/hooks';
import { capitalizeWords, getObjectValue, getStringValue } from 'utilities';
import { getColumnLetter } from 'utilities/alphabet-utilities';
import getCancelDialogOptions from 'utilities/getCancelDialogOptions';
import { Cell } from '../../ScalarSpreadsheet/utilities/Cell';
import FeaturedSpreadsheetContext from '../context/FeaturedSpreadsheetContext';
import { copyToClipboard, getCellRef, getValuesToCopy, hasCellRowSpan } from '../utilities/utilities';

const StyledRowIndicator = styled('div')(({ theme }) => ({
  position: 'absolute',
  width: '12px',
  marginLeft: '-12px',
  backgroundColor: theme.palette.primary.main,
  borderRadius: '3px 0px 0px 3px',
  display: 'flex',
  flexWrap: 'wrap',
  alignContent: 'center',
  zIndex: 1,
}));

interface StyledMenuButtonProps {
  emphasizeViewerFreezeAction?: boolean;
}

const StyledMenuButton = styled(IconButton, {
  shouldForwardProp: prop => prop !== 'emphasizeViewerFreezeAction',
})<StyledMenuButtonProps>(({ theme, emphasizeViewerFreezeAction }) => ({
  color: theme.palette.common.white,
  height: emphasizeViewerFreezeAction ? '18px' : undefined,
  marginLeft: emphasizeViewerFreezeAction ? '-3px' : undefined,
  maxWidth: emphasizeViewerFreezeAction ? '18px' : undefined,
  minWidth: emphasizeViewerFreezeAction ? '18px' : undefined,
  padding: '0px',
  '& > svg': emphasizeViewerFreezeAction
    ? {
      fontSize: '18px !important',
      height: '18px !important',
    }
    : {},
}));

const StyledMoreVertIcon = styled(MoreVert)(({ theme }) => ({
  fill: theme.palette.common.white,
  width: 'auto !important',
  fontSize: '0.8rem',
}));

const StyledKeyboardArrowRightIcon = styled(KeyboardArrowRight)(({ theme }) => ({
  fill: theme.palette.common.white,
  width: 'auto !important',
  fontSize: '0.8rem',
}));

const StyledDeleteMenuItem = styled(MenuItem)(({ theme }) => ({
  color: theme.palette.error.main,
}));

interface StyledMenuProps {
  compactViewerOptions: boolean;
}

const StyledMenu = styled(Menu, {
  shouldForwardProp: prop => prop !== 'compactViewerOptions',
})<StyledMenuProps>(({ compactViewerOptions }) =>
  compactViewerOptions
    ? {
      '& .MuiPaper-root': {
        minWidth: '188px',
        maxWidth: '220px',
      },
      '& .MuiList-root': {
        padding: '4px 0',
      },
      '& .MuiMenuItem-root': {
        minHeight: '28px',
        padding: '3px 8px 3px 10px',
        fontSize: '12px',
        lineHeight: 1.25,
        gap: '8px',
        whiteSpace: 'nowrap',
      },
      '& .MuiMenuItem-root > .MuiSvgIcon-root': {
        flex: '0 0 auto',
        fontSize: '16px',
      },
      '& .MuiCheckbox-root': {
        marginLeft: 'auto',
        padding: '2px',
      },
      '& .MuiCheckbox-root .MuiSvgIcon-root': {
        fontSize: '16px',
      },
    }
    : {}
);

interface IRowIndicatorProps {
  frozenPaneCellKey?: string;
  onToggleFrozenPaneAtCell?: (cellKey: string) => void;
  tableRef: React.RefObject<HTMLElement | null>;
}

interface IndicatorPosition {
  top: number;
  height: number;
}

const areCellsInSameSpreadsheetRow = (
  tableRef: React.RefObject<HTMLElement | null>,
  firstCellKey?: string,
  secondCellKey?: string
): boolean => {
  const table = tableRef.current;
  if (!table || !firstCellKey || !secondCellKey) {
    return false;
  }

  const ownerWindow = table.ownerDocument.defaultView ?? window;
  const getCellRowIndex = (cellKey: string) =>
    table
      .querySelector<HTMLElement>(`#${ownerWindow.CSS.escape(`CELL-${cellKey}`)}`)
      ?.closest('tr')
      ?.querySelector<HTMLElement>('td[data-row-index]')?.dataset.rowIndex;

  const firstCellRowIndex = getCellRowIndex(firstCellKey);
  const secondCellRowIndex = getCellRowIndex(secondCellKey);
  return Boolean(firstCellRowIndex && firstCellRowIndex === secondCellRowIndex);
};

// The titles column is always the first column (index 0) in the spreadsheet grid.
// Used to look up cell keys (e.g., "A1") when iterating rows for multi-row operations.
const TITLES_COLUMN_INDEX = 0;

// Calculates the blue indicator bar position for a single selected row.
const computeSingleRowPosition = (
  tableRef: React.RefObject<HTMLElement | null>,
  cells: TCells,
  cellInfo: Cell,
  startColumn: number,
  startRow: number,
  allowVisualRowSpan: boolean
): IndicatorPosition | null => {
  const columnLetter = getColumnLetter(startColumn);
  const cellKey = cellInfo.key ?? `${columnLetter}${startRow}`;
  const cellRef = getCellRef(tableRef as React.RefObject<HTMLElement>, cellKey);
  const hasRowSpan = hasCellRowSpan(cells, cellKey, cellRef);
  if (!cellRef || (hasRowSpan && !allowVisualRowSpan)) {
    return null;
  }
  return {
    top: cellRef.offsetTop,
    height: cellRef.offsetHeight,
  };
};

// Calculates the blue indicator bar position spanning multiple selected rows.
// The selection can go in either direction, so min/max keeps the range stable.
const computeMultiRowPosition = (
  tableRef: React.RefObject<HTMLElement | null>,
  cellInfo: Cell,
  endCellInfo: Cell | undefined,
  start: { i: number; j: number },
  end: { i: number; j: number }
): IndicatorPosition | null => {
  if (!endCellInfo) {
    return null;
  }
  const startColumnLetter = getColumnLetter(start.j);
  const endColumnLetter = getColumnLetter(end.j);
  const startCellKey = cellInfo.key ?? `${startColumnLetter}${start.i}`;
  const startCellRef = getCellRef(tableRef as React.RefObject<HTMLElement>, startCellKey);
  const endCellKey = endCellInfo.key ?? `${endColumnLetter}${end.i}`;
  const endCellRef = getCellRef(tableRef as React.RefObject<HTMLElement>, endCellKey);

  if (!startCellRef || !endCellRef) {
    return null;
  }
  const minTop = Math.min(startCellRef.offsetTop, endCellRef.offsetTop);
  const bottomEl = startCellRef.offsetTop > endCellRef.offsetTop ? startCellRef : endCellRef;
  const bottom = bottomEl.offsetTop + bottomEl.offsetHeight;
  const top = minTop;
  const height = bottom - minTop;
  return { top, height };
};

const RowIndicator = ({ frozenPaneCellKey, onToggleFrozenPaneAtCell, tableRef }: IRowIndicatorProps) => {
  const [showIndicator, setShowIndicator] = useState(false);
  const [indicatorPosition, setIndicatorPosition] = useState<IndicatorPosition>();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);
  const [cell, setCell] = useState<ICell>();
  const [multipleValue, setMultipleValue] = useState(0);
  const {
    selectedCell,
    setClearSelected,
    setSelectedCell,
    cells,
    displayRowIndicator,
    allowReorderRows,
    allowCopyRows,
    allowAddMultipleRows,
    addMultipleRows,
    allowAddSingleRow,
    addSingleRow,
    displayLegend,
    activeCell,
    tableData,
    allowDeleteRow,
    allowMergedSelectionIndicatorSpan,
    deleteRowFn,
    deleteMultipleRowsFn,
    allowChangeDataType,
    changeRowDataTypeFn,
    format,
  } = useContext(FeaturedSpreadsheetContext);
  // Tracks whether the current selection spans multiple rows, so the delete handler
  // can decide between single-row vs batch-delete behavior
  const [isMultiRowSelection, setIsMultiRowSelection] = useState(false);
  const { infoNotification, errorNotification } = useResponse();
  const dialogContext = useContext(DialogContext);
  const hasConfiguredRowActions = Boolean(
    allowAddMultipleRows ||
      allowAddSingleRow ||
      allowChangeDataType ||
      allowCopyRows ||
      allowDeleteRow ||
      allowReorderRows ||
      onToggleFrozenPaneAtCell
  );

  const getUpdatedCell = useCallback(
    (cellInfo: any) => {
      if (!cellInfo || !cells) {
        return null;
      }
      const { key } = cellInfo;
      const updatedCell = cells[key];
      return updatedCell || cellInfo;
    },
    [cells]
  );

  // Recompute the indicator position whenever the selection changes.
  // For multi-row selections, only show the indicator if the table supports batch deletion,
  // since the multi-row indicator is exclusively for the batch-delete workflow.
  useEffect(() => {
    if (!selectedCell || !displayRowIndicator) {
      setIndicatorPosition(undefined);
      setIsMultiRowSelection(false);
      setShowIndicator(false);
      return;
    }
    const { start, end, cellInfo, endCellInfo } = selectedCell;
    const updatedCell = getUpdatedCell(cellInfo);
    if (!updatedCell) {
      setIndicatorPosition(undefined);
      setIsMultiRowSelection(false);
      setShowIndicator(false);
      return;
    }
    setCell(updatedCell);
    const { j: startColumn, i: startRow } = start;
    const { i: endRow } = end;
    const isMultiRow = startRow !== endRow;

    if (!isMultiRow) {
      setIsMultiRowSelection(false);
      const position = computeSingleRowPosition(
        tableRef,
        cells,
        cellInfo,
        startColumn,
        startRow,
        Boolean(allowMergedSelectionIndicatorSpan && !hasConfiguredRowActions)
      );
      if (position) {
        setIndicatorPosition(position);
        setShowIndicator(true);
      } else {
        setIndicatorPosition(undefined);
        setShowIndicator(false);
      }
    } else if (deleteMultipleRowsFn) {
      setIsMultiRowSelection(true);
      const position = computeMultiRowPosition(tableRef, cellInfo, endCellInfo, start, end);
      if (position) {
        setIndicatorPosition(position);
        setShowIndicator(true);
      } else {
        setIndicatorPosition(undefined);
        setShowIndicator(false);
      }
    } else {
      // Multi-row selection without batch-delete support: hide indicator
      setIndicatorPosition(undefined);
      setShowIndicator(false);
    }
  }, [
    selectedCell,
    cells,
    tableRef,
    displayRowIndicator,
    displayLegend,
    activeCell,
    getUpdatedCell,
    deleteMultipleRowsFn,
    allowMergedSelectionIndicatorSpan,
    hasConfiguredRowActions,
  ]);

  const onCloseDropdown = useCallback(() => setAnchorEl(null), [setAnchorEl]);

  const handleOnMoreOptionsClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    },
    [setAnchorEl]
  );

  const handleOnCopyClick = useCallback(() => {
    onCloseDropdown();
    if (!selectedCell) {
      return;
    }
    const { rowNumber } = selectedCell;
    if (rowNumber) {
      const valuesToCopy = getValuesToCopy(
        cells,
        rowNumber,
        false,
        format?.currency?.exchangeRate,
        format?.currency?.units
      );
      copyToClipboard(valuesToCopy, infoNotification, errorNotification);
    }
  }, [cells, selectedCell, infoNotification, errorNotification, onCloseDropdown, format]);

  const removeIndicator = useCallback(() => {
    setClearSelected(true);
    setSelectedCell(undefined);
    setIndicatorPosition(undefined);
    setIsMultiRowSelection(false);
    setShowIndicator(false);
  }, [setClearSelected, setSelectedCell]);

  const handleToggleFrozenPane = useCallback(() => {
    onCloseDropdown();
    if (cell?.key && onToggleFrozenPaneAtCell) {
      const selectedCellKey = String(cell.key);
      onToggleFrozenPaneAtCell(
        areCellsInSameSpreadsheetRow(tableRef, frozenPaneCellKey, selectedCellKey) && frozenPaneCellKey
          ? frozenPaneCellKey
          : selectedCellKey
      );
      removeIndicator();
    }
  }, [cell?.key, frozenPaneCellKey, onCloseDropdown, onToggleFrozenPaneAtCell, removeIndicator, tableRef]);

  const handleOnAddSingleRowClick = useCallback(() => {
    onCloseDropdown();
    if (addSingleRow) {
      addSingleRow();
    }
  }, [addSingleRow, onCloseDropdown]);

  const handleOnAddMultipleClick = () => {
    onCloseDropdown();
    if (addMultipleRows) {
      addMultipleRows(multipleValue);
    }
  };

  const deleteRowCb = useCallback(
    (company: any) => {
      if (deleteRowFn) {
        deleteRowFn(company);
      }
      removeIndicator();
    },
    [deleteRowFn, removeIndicator]
  );

  const showDeleteDialog = useCallback(
    (company: any, preferredMessage: string | null = null) => {
      const companyName = getStringValue(company.target_name ?? company.name);
      const CUSTOM_MESSAGE = `Are you sure you want to delete ${
        company.target_name === '' ? 'this' : ''
      } ${companyName}?`;

      const { showDialog } = dialogContext;

      const ContentComponent = <ConfirmationDelete customMessage={preferredMessage ?? CUSTOM_MESSAGE} />;
      showDialog(
        getCancelDialogOptions(
          () => ContentComponent,
          () => deleteRowCb(company)
        )
      );
    },
    [dialogContext, deleteRowCb]
  );

  const onDeleteTransactionCompany = useCallback(() => {
    if (!tableData?.approach) {
      return;
    }
    const {
      approach: { valuations_approach_gpt: valuationsApproachGpt },
    } = tableData;
    if (valuationsApproachGpt && cell) {
      const { gpt_transactions: gptTransactions } = valuationsApproachGpt;
      if (gptTransactions) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        const transaction = gptTransactions.find(item => item.row_ref === cell.alias || item.row_ref === cell.row_ref);
        if (transaction) {
          showDeleteDialog(transaction);
        }
      }
    }
  }, [cell, tableData, showDeleteDialog]);

  // todo: this logic is too specific too the component, we should refactor
  const onDeleteComparisonCompany = useCallback(() => {
    if (tableData?.approach) {
      const {
        approach: { valuations_approach_gpc: valuationsApproachGpc },
      } = tableData;
      if (valuationsApproachGpc && cell) {
        const { gpc_comparison: gpcComparison } = valuationsApproachGpc;
        if (gpcComparison) {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          const comparison = gpcComparison.find(({ cap_iq_id: capIdId }) =>
            [cell.alias, cell.titleAlias].includes(capIdId)
          );
          if (comparison) {
            showDeleteDialog(comparison);
          }
        }
      }
    }
  }, [cell, tableData, showDeleteDialog]);

  // todo: logic should not be specific to the component, we should refactor
  const onDeleteKpi = useCallback(() => {
    if (tableData?.companyKPIs === undefined) {
      return;
    }
    const { companyKPIs } = tableData;
    if (companyKPIs?.length && cell) {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const kpi = companyKPIs.find(({ alias }) => cell.alias === alias);
      if (kpi) {
        showDeleteDialog(
          kpi,
          'Are you sure to want delete this KPI? Deleting it will affect all the measurement dates for this company.'
        );
      }
    }
  }, [cell, tableData, showDeleteDialog]);

  const onDeleteRow = useCallback(() => {
    const { data: cellData } = getObjectValue(cell);

    if (cell && !isEmpty(cellData)) {
      showDeleteDialog(cellData);
    }
  }, [cell, showDeleteDialog]);

  // Invoked after the user confirms the batch-delete dialog; delegates to the
  // page-level handler (e.g., onRemoveMultiple in GuidelinePublicCompanies)
  const deleteMultipleRowsCb = useCallback(
    (comparisons: any[]) => {
      if (deleteMultipleRowsFn) {
        deleteMultipleRowsFn(comparisons);
      }
      removeIndicator();
    },
    [deleteMultipleRowsFn, removeIndicator]
  );

  // Collects all GPC comparison items within the selected row range
  // and shows a batch-delete confirmation dialog
  const onDeleteMultipleGpcComparisons = useCallback(() => {
    if (!selectedCell || !tableData?.approach || !deleteMultipleRowsFn) {
      return;
    }
    const { start, end } = selectedCell;
    // Normalize direction: selection can go top-to-bottom or bottom-to-top
    const startRow = Math.min(start.i, end.i);
    const endRow = Math.max(start.i, end.i);
    const { valuations_approach_gpc: valuationsApproachGpc } = tableData.approach;
    if (!valuationsApproachGpc) {
      return;
    }
    const { gpc_comparison: gpcComparison } = valuationsApproachGpc;
    if (!gpcComparison) {
      return;
    }

    const comparisonsToDelete: any[] = [];
    for (let row = startRow; row <= endRow; row++) {
      // Look up each row's title cell (column A) to find the company identifier
      const columnLetter = getColumnLetter(TITLES_COLUMN_INDEX);
      const cellKey = `${columnLetter}${row}`;
      const rowCell = cells[cellKey];
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (rowCell?.isGpcRow) {
        const comparison = gpcComparison.find(
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          ({ cap_iq_id: capIqId }) => [rowCell.alias, rowCell.titleAlias].includes(capIqId)
        );
        if (comparison) {
          comparisonsToDelete.push(comparison);
        }
      }
    }

    if (comparisonsToDelete.length === 0) {
      return;
    }

    const names = comparisonsToDelete.map(c => getStringValue(c.target_name ?? c.name)).filter(Boolean);
    const message =
      comparisonsToDelete.length === 1
        ? `Are you sure you want to delete ${names[0]}?`
        : `Are you sure you want to delete these ${comparisonsToDelete.length} companies?\n\n${names.join(', ')}`;

    const { showDialog } = dialogContext;
    const ContentComponent = <ConfirmationDelete customMessage={message} />;
    showDialog(
      getCancelDialogOptions(
        () => ContentComponent,
        () => deleteMultipleRowsCb(comparisonsToDelete)
      )
    );
  }, [selectedCell, tableData, deleteMultipleRowsFn, cells, dialogContext, deleteMultipleRowsCb]);

  //  todo: this logic is too specific to the component, we should refactor
  // Routes to the appropriate delete handler based on whether multiple rows are
  // selected (batch delete) vs a single row with a specific row type (GPC/GPT/KPI)
  const handleOnDeleteRowClick = useCallback(() => {
    onCloseDropdown();

    // Multi-row selection takes precedence: delegate to batch-delete flow
    if (isMultiRowSelection && deleteMultipleRowsFn) {
      onDeleteMultipleGpcComparisons();
      return;
    }

    if (!cell) {
      return;
    }

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    if (cell.isGptRow) {
      onDeleteTransactionCompany();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } else if (cell.isGpcRow) {
      onDeleteComparisonCompany();

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
    } else if (cell.isKpiRow) {
      onDeleteKpi();
    } else {
      onDeleteRow();
    }
  }, [
    onCloseDropdown,
    cell,
    isMultiRowSelection,
    deleteMultipleRowsFn,
    onDeleteMultipleGpcComparisons,
    onDeleteTransactionCompany,
    onDeleteComparisonCompany,
    onDeleteKpi,
    onDeleteRow,
  ]);

  // todo: this logic is too specific to the component, we should refactor
  const handleChangeDataType = useCallback(() => {
    if (!cell) {
      return;
    }
    const { showDialog, wrapperProps } = dialogContext;
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    const currentDataType = cell.sheet?.tableData?.companyKPIs?.find(({ alias }) => alias === cell.alias)?.data_type;
    const dataTypeDialogProps: ConfirmationWithRadioProps = {
      confirmationBaseFn: changeRowDataTypeFn,
      options: kpiDataTypes.map(([value, label]) => ({ value, label: capitalizeWords(label) })),
      cell,
      dialogTitle: 'KPI data types',
      defaultValue: currentDataType,
      open: true,
      onClose: wrapperProps.onClose,
      size: 'xs',
    };
    const wrapper = () => <ConfirmationWithRadioWrapper {...dataTypeDialogProps} />;
    showDialog({ wrapper });
    onCloseDropdown();
  }, [cell, changeRowDataTypeFn, dialogContext, onCloseDropdown]);

  // todo: this logic is too specific to the component, we should refactor
  // For multi-row: only need a batch handler; for single-row: also check the cell type
  // since not every row type supports deletion (e.g., header/aggregate rows)
  const allowDelete = useMemo(() => {
    if (isMultiRowSelection) {
      return allowDeleteRow && deleteMultipleRowsFn;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    return (
      allowDeleteRow && deleteRowFn && (cell?.isGptRow || cell?.isGpcRow || cell?.isKpiRow || cell?.isDeletableRow)
    );
  }, [allowDeleteRow, deleteRowFn, deleteMultipleRowsFn, cell, isMultiRowSelection]);

  // With this function we will determine if do we have to show or not the dropdown menu on the row indicator
  const showMenuOptions = useMemo(
    () => Boolean(onToggleFrozenPaneAtCell) || allowCopyRows || allowDelete,
    [allowCopyRows, allowDelete, onToggleFrozenPaneAtCell]
  );
  const isCurrentFrozenPaneBoundary = areCellsInSameSpreadsheetRow(
    tableRef,
    frozenPaneCellKey,
    cell?.key ? String(cell.key) : undefined
  );
  const freezeRowLabel = isCurrentFrozenPaneBoundary ? 'Unfreeze this row' : 'Freeze this row';

  if (!showIndicator) {
    return null;
  }

  return (
    <StyledRowIndicator
      id={`row-indicator-${cell?.key}`}
      style={{ top: indicatorPosition?.top, height: indicatorPosition?.height }}>
      {showIndicator && allowReorderRows && (
        <StyledMenuButton>
          <StyledMoreVertIcon />
        </StyledMenuButton>
      )}
      {showIndicator && showMenuOptions && (
        <StyledMenuButton
          emphasizeViewerFreezeAction={Boolean(onToggleFrozenPaneAtCell)}
          onClick={handleOnMoreOptionsClick}>
          <StyledKeyboardArrowRightIcon />
        </StyledMenuButton>
      )}
      {showMenuOptions && (
        <Popper open={open} anchorEl={anchorEl} id={open ? 'transitions-popper' : undefined}>
          <StyledMenu
            compactViewerOptions={Boolean(onToggleFrozenPaneAtCell)}
            anchorEl={anchorEl}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            keepMounted
            open={Boolean(anchorEl)}
            onClose={onCloseDropdown}>
            {onToggleFrozenPaneAtCell && (
              <MenuItem data-testid="toggle-frozen-rows" onClick={handleToggleFrozenPane}>
                <PushPinOutlined />
                {freezeRowLabel}
                <Checkbox
                  aria-label={freezeRowLabel}
                  checked={isCurrentFrozenPaneBoundary}
                  onChange={handleToggleFrozenPane}
                  onClick={event => event.stopPropagation()}
                  size="small"
                />
              </MenuItem>
            )}
            {allowCopyRows && <MenuItem onClick={handleOnCopyClick}>COPY</MenuItem>}
            {allowAddSingleRow && addSingleRow && <MenuItem onClick={handleOnAddSingleRowClick}>ADD NEW</MenuItem>}
            {allowAddMultipleRows && addMultipleRows && (
              <AddMultipleMenuItem
                onAddMultiple={handleOnAddMultipleClick}
                multipleValue={multipleValue}
                setMultipleValue={setMultipleValue}
              />
            )}
            {allowChangeDataType && <MenuItem onClick={handleChangeDataType}>CHANGE DATA TYPE</MenuItem>}
            {allowDelete && <StyledDeleteMenuItem onClick={handleOnDeleteRowClick}>DELETE</StyledDeleteMenuItem>}
          </StyledMenu>
        </Popper>
      )}
    </StyledRowIndicator>
  );
};

RowIndicator.propTypes = {
  tableRef: PropTypes.oneOfType([
    // Either a function
    PropTypes.func,
    // Or the instance of a DOM native element (see the note about SSR)
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
};

export default RowIndicator;
